import type { BrandConfig } from '../branding/types';

/**
 * List + revoke paired machines. Calls Keep's service-identities API
 * filtered to this whitelabel's OAuth client. Re-pairing on a new machine
 * creates a new identity under the same role binding; nothing here
 * revokes implicitly.
 */
export interface PairedMachine {
  id: string;
  name: string;
  displayName: string;
  hostname?: string;
  user?: string;
  pairedAt?: string;
  lastSeenAt?: string;
  active: boolean;
}

export interface PairingsApiOptions {
  brand: BrandConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

export async function listPairings(opts: PairingsApiOptions): Promise<PairedMachine[]> {
  const fetcher = opts.fetcher ?? globalThis.fetch;
  const url = `${opts.brand.keepRegistration.keepApiUrl.replace(/\/$/, '')}/api/me/service-identities?client_id=${encodeURIComponent(opts.brand.keepRegistration.oauthClientId)}`;
  const response = await fetcher(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${opts.accessToken}` },
  });
  return parseJson<PairedMachine[]>(response);
}

export async function revokePairing(
  opts: PairingsApiOptions & { identityId: string },
): Promise<void> {
  const fetcher = opts.fetcher ?? globalThis.fetch;
  const url = `${opts.brand.keepRegistration.keepApiUrl.replace(/\/$/, '')}/api/service-identities/${encodeURIComponent(opts.identityId)}`;
  const response = await fetcher(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${opts.accessToken}` },
  });
  if (!response.ok) {
    throw new Error(`Revoke failed with status ${response.status}`);
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new Error(`Keep returned non-JSON (status ${response.status}).`);
  }
  if (!response.ok) {
    const err = body as { error?: { message?: string } };
    throw new Error(err.error?.message ?? `Keep returned status ${response.status}.`);
  }
  return (body as { data: T }).data;
}
